import clock from '@static/vectors/sentinel/clock.svg';
import { formatDuration } from '@utils/timeUtils';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppSelector } from 'universal-scripts';
import './ProgressBar.css';

const MIN_EXPECTED_STEPS: Record<string, number> = {
	detect: 3, // video_upload, video_analysis, claim_selection
	verify: 5, // search_claim, search_news_db, search_news_web, search_web_pages, verify_claim
	respond: 2 // risk_analysis, suggest_actions
};

export default function ProgressBar() {
	const { currentPhase, status, phases, timeline } = useAppSelector((state) => state.sentinel);

	const calculatedProgress = useMemo(() => {
		if (!currentPhase) return 0;
		const stepScores = new Map<string, number>();

		const relevantEvents = Object.values(timeline).filter((e) => e.phase === currentPhase && e.stepId);

		for (const event of relevantEvents) {
			let score = 0;
			if (event.stepStatus === 'completed') {
				score = 1;
			} else if (['in_progress', 'running', 'processing'].includes(event.stepStatus as string)) {
				score = 0.1;
			}
			stepScores.set(event.stepId!, score);
		}

		let totalScore = 0;
		for (const score of stepScores.values()) totalScore += score;

		const denominator = Math.max(stepScores.size, MIN_EXPECTED_STEPS[currentPhase] || 1);
		return Math.min(Math.round((totalScore / denominator) * 100), 100);
	}, [currentPhase, timeline]);

	const elapsedTimeString = useMemo(() => {
		if (status !== 'completed' && status !== 'error') return null;
		const start = phases.detect.startedAt;
		const end = phases.respond.completedAt || phases.verify.completedAt || phases.detect.completedAt;
		return start && end ? formatDuration(end - start) : null;
	}, [status, phases]);

	if (!currentPhase && status === 'idle') {
		return (
			<div className="waiting-progress" id="sidebar-progress">
				<img src={clock} alt="clock icon" />
				<FormattedMessage id="sentinel.sidebar-progress.waiting" />
			</div>
		);
	}

	if ((status === 'completed' || status === 'error') && elapsedTimeString) {
		return (
			<div id="sidebar-progress" className={`finished status-${status}`}>
				<div className="time-result">
					<div className="time-text">
						<small>
							<FormattedMessage id="sentinel.sidebar-progress.total-time" />
						</small>
						<strong> {elapsedTimeString}</strong>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div id="sidebar-progress">
			<div className="progress-info">
				<small className="progress-phase">
					{currentPhase && <FormattedMessage id={`sentinel-home.phase.${currentPhase}.title`} />} {calculatedProgress}%
				</small>
			</div>
			<div
				style={{ '--percentage-progress': `${calculatedProgress}%` } as React.CSSProperties}
				title={`${calculatedProgress}%`}
				className="progress-bar"
			>
				<div className="progress-fill" style={{ width: `${calculatedProgress}%` }} />
			</div>
		</div>
	);
}
